import { ReadonlyVec2, vec2 } from 'gl-matrix';
import { AnnotationPlane } from '~/domain/annotation/annotation';
import {
    euclideanDistanceMm,
    PointMm,
    toPointMm,
} from '~/domain/geometry/patientGeometry';
import {
    paneImageOf,
    samePaneImage,
    ShownPaneImage,
} from '~/engine/view/paneImage';
import type { PlaneView } from '~/engine/view/views/PlaneView';
import { TextureView } from '~/engine/view/TextureView';
import { localization } from '~/i18n';
import { enqueueSnackbar } from '~/utils/notifications';
import {
    EventHandlerResult,
    EventHandlerResultType,
} from '../EventHandlerResult';
import { ModifierKeys } from '../ModifierKeys';
import {
    MOUSE_BUTTON_LEFT,
    MOUSE_BUTTON_RIGHT,
    MOUSE_BUTTONS_LEFT_MASK,
} from '../MouseButton';
import { CrosshairInteractionMode, Tool } from '../Tool';

/**
 * Narrowing without importing PlaneView: the tools are in an import cycle with
 * it via ViewManager, and a runtime import leaves this base undefined when a
 * tool module loads first.
 */
function isPlaneView(view: TextureView): view is PlaneView {
    return view.isPlaneView;
}

function handled(): EventHandlerResult {
    return new EventHandlerResult(EventHandlerResultType.EVENT_HANDLED);
}

function notHandled(): EventHandlerResult {
    return new EventHandlerResult(EventHandlerResultType.EVENT_NOT_HANDLED);
}

/**
 * Shown when a placement press lands on a pane that draws no image: a
 * secondary-layer tile with nothing fused, or a tile of an Environment
 * with no image. There is no image to key a record to. Read in the
 * viewer's language at the time of the press.
 */
function paneShowsNoImageMessage(): string {
    return localization.t('tool.disabled.paneShowsNoImage');
}

/**
 * Shown when a later press of a draft finds the pane showing another image
 * than the one the draft was started on, or the same image moved by a
 * registration edit: the draft is dropped rather than committed against the
 * image now shown. Read in the viewer's language at the time of the press.
 */
function paneImageChangedMessage(): string {
    return localization.t('annotation.cancelled.imageChanged');
}

/**
 * A left release this far (screen px) from its press places the next point;
 * closer is a click. The same distance tells a press on the point just placed
 * from one beside it. Above the slide of a trackpad click, and erring that
 * way on purpose: a click misread as a drag records a measurement nobody
 * placed, while a drag misread as a click places nothing beyond where it
 * pressed.
 */
const DRAG_PLACEMENT_THRESHOLD_PX = 10;

/** A left press whose release is still to come. */
interface PendingPress {
    /** Window position, to tell the release of a click from that of a drag. */
    readonly coordinates: ReadonlyVec2;
    /**
     * The point the press would have placed, held back because it landed on
     * the point just placed: the release places it for a click, or the
     * release point for a drag. Null when the press placed at once.
     */
    readonly heldPointMm: PointMm | null;
}

/**
 * The plane of `view` in the pane's frame, keyed to the series of the image
 * the pane shows, or null when the view has no orientation to name it by.
 */
function annotationPlaneOf(
    view: PlaneView,
    pane: ShownPaneImage
): AnnotationPlane | null {
    const orientation = view.label;
    if (orientation === undefined) {
        return null;
    }
    return {
        seriesInstanceUid: pane.seriesUid,
        orientation,
        viewDirection: view.viewDirection,
        planeCoordinateMm: view.planeCoordinates,
    };
}

/**
 * Placement lifecycle shared by the annotation tools: bind the draft to one
 * view, hand patient-space mm points to the subclass, discard on Escape,
 * right-click or deactivation.
 *
 * A record is keyed to the image the pressed pane shows, the primary or the
 * fused secondary drawn alone, and is stored in that image's own frame. The
 * subclass works in the pane's frame and gets the pane answer of the first
 * press, which it maps its draft and its record through. Every later
 * placement first checks that the pane still shows that image in the same
 * place, and drops the draft with a warning when it does not.
 *
 * A point is placed by clicking. A tool that opts in via
 * {@link placesPointOnDragRelease} also places one by releasing a press that
 * has been dragged clear of where it started, so both the click-click and the
 * press-drag-release habits produce the same annotation. For that tool a
 * press on the point just placed waits for its release, so a drag can
 * continue from the point without first placing another beside it, while a
 * click there still places where it pressed.
 *
 * The subclass owns its points and its commit rule. This class owns only "is a
 * placement in progress, and in which view", which the engine queries to block
 * slice-scroll, pan and crosshair drags.
 */
export abstract class AnnotationPlacementTool extends Tool {
    /** The view the draft is bound to; null when idle. */
    private _view: PlaneView | null = null;

    /** What the bound view showed at the first press; null when idle. */
    private _pane: ShownPaneImage | null = null;

    /**
     * The left press awaiting its release. Forgotten as soon as the button is
     * seen up over any view, the cursor leaves the viewer, or a right-click
     * undoes a point: a release outside every viewport is never delivered,
     * and the press must not outlive it or the gesture it belonged to.
     */
    private _press: PendingPress | null = null;

    /** Blocks slice-scroll and mouse-leave deactivation mid-draft. */
    public override isActionInProgress(): boolean {
        return this._view !== null;
    }

    /**
     * False even while a point is being dragged into place: what is being
     * built is a few positions, not a line the user paints pixel by pixel, so
     * Ctrl+wheel zoom and windowing behave the same during a drag as they do
     * between two clicks.
     */
    public override isStrokeInProgress(): boolean {
        return false;
    }

    /** The view being placed in, so pan is blocked in other viewports. */
    public override getDrawingView(): PlaneView | undefined {
        return this._view ?? undefined;
    }

    /** A draft bound to `view` is discarded without a message. */
    public override cancelActionIn(view: PlaneView): EventHandlerResult {
        if (!this._view || view !== this._view) {
            return notHandled();
        }
        this.cancelDraft();
        return handled();
    }

    public override getCrosshairInteractionMode(
        _isDrawingActiveInOtherView: boolean
    ): CrosshairInteractionMode {
        // Suppress crosshair interaction while placing points so a drag on a
        // crosshair handle cannot interfere; full interaction when idle.
        return this._view
            ? CrosshairInteractionMode.None
            : CrosshairInteractionMode.Full;
    }

    /**
     * A pane that draws no image is refused, with the not-allowed cursor
     * over it: nothing there can be measured.
     */
    public override refusalFor(view: PlaneView): string | undefined {
        return paneImageOf(view).kind === 'none'
            ? paneShowsNoImageMessage()
            : undefined;
    }

    /** Claim right-click during a draft so global windowing yields to undo. */
    public override usesButtonDuringAction(button: number): boolean {
        return button === MOUSE_BUTTON_RIGHT && this._view !== null;
    }

    public override onMousePress(
        button: number,
        _modifierKeys: ModifierKeys,
        coordinates: ReadonlyVec2,
        view: TextureView
    ): EventHandlerResult {
        if (button !== MOUSE_BUTTON_LEFT) {
            // Consume right-click while drafting so it cannot trigger
            // windowing; the actual undo happens on release.
            return button === MOUSE_BUTTON_RIGHT && this._view
                ? handled()
                : notHandled();
        }
        if (!isPlaneView(view)) {
            return notHandled();
        }
        // The draft is bound to the view of its first point; ignore (but
        // consume) clicks in other viewports so they can neither pan nor start
        // a second, cross-plane annotation.
        if (this._view) {
            if (view !== this._view) {
                return handled();
            }
            if (!this.paneStillShown(view)) {
                return handled();
            }
            const pointMm = this.patientPointAt(view, coordinates);
            const held = this.isOnLastPlacedPoint(view, pointMm);
            this._press = {
                coordinates: vec2.clone(coordinates),
                heldPointMm: held ? pointMm : null,
            };
            if (!held) {
                this.placePoint(view, pointMm);
            }
            return handled();
        }
        // Consumed, not passed on: a press the user meant as a placement
        // must not pan the tile instead, and the warning says why nothing
        // was placed.
        const pane = paneImageOf(view);
        if (pane.kind === 'none') {
            enqueueSnackbar(paneShowsNoImageMessage(), { variant: 'warning' });
            return handled();
        }
        const refusal = this.placementRefusal(view, pane);
        if (refusal !== null) {
            enqueueSnackbar(refusal, { variant: 'warning' });
            return handled();
        }
        const plane = annotationPlaneOf(view, pane);
        if (!plane) {
            return notHandled();
        }
        // Bound before startPlacement so a subclass may release it and place its next point elsewhere.
        this._view = view;
        this._pane = pane;
        const pointMm = this.patientPointAt(view, coordinates);
        if (!this.startPlacement(view, plane, pointMm, pane)) {
            this._view = null;
            this._pane = null;
            return notHandled();
        }
        this._press = {
            coordinates: vec2.clone(coordinates),
            heldPointMm: null,
        };
        return handled();
    }

    public override onMouseMove(
        coordinates: ReadonlyVec2,
        buttons: number,
        view: TextureView
    ): EventHandlerResult {
        if ((buttons & MOUSE_BUTTONS_LEFT_MASK) === 0) {
            this._press = null;
        }
        if (!this._view || view !== this._view) {
            return notHandled();
        }
        this.moveCursor(
            this._view,
            this.patientPointAt(this._view, coordinates)
        );
        return handled();
    }

    /**
     * The button may come up, and go down again, out of sight: a release
     * over this view must not be read as the end of a press it never saw.
     */
    public override onMouseLeave(): void {
        this._press = null;
    }

    /** The view moved under a still cursor; no button state is reported. */
    public override onViewChanged(
        coordinates: ReadonlyVec2,
        view: TextureView
    ): void {
        if (this._view && view === this._view) {
            this.moveCursor(
                this._view,
                this.patientPointAt(this._view, coordinates)
            );
        }
    }

    public override onMouseRelease(
        button: number,
        coordinates: ReadonlyVec2,
        view: TextureView
    ): EventHandlerResult {
        if (button === MOUSE_BUTTON_RIGHT && this._view) {
            // The undo ends any left press still held: its release would
            // otherwise put back the point the undo just removed.
            this._press = null;
            this.undoPoint();
            return handled();
        }
        if (button !== MOUSE_BUTTON_LEFT) {
            return notHandled();
        }
        const press = this._press;
        this._press = null;
        const draftView = this._view;
        // Releasing in another viewport, or after the draft is already
        // finished or dropped, places nothing: only the press that opened the
        // draft can close it, in the view it belongs to.
        if (!press || !draftView || view !== draftView) {
            return notHandled();
        }
        const dragged =
            vec2.distance(press.coordinates, coordinates) >=
            DRAG_PLACEMENT_THRESHOLD_PX;
        if (press.heldPointMm !== null) {
            this.placeIfPaneStillShown(
                draftView,
                dragged
                    ? this.patientPointAt(draftView, coordinates)
                    : press.heldPointMm
            );
            return handled();
        }
        if (!this.placesPointOnDragRelease() || !dragged) {
            return notHandled();
        }
        this.placeIfPaneStillShown(
            draftView,
            this.patientPointAt(draftView, coordinates)
        );
        return handled();
    }

    public override onKeyPress(
        key: string,
        _modifierKeys: ModifierKeys,
        _view: TextureView
    ): EventHandlerResult {
        if (key === 'Escape' && this._view) {
            this.cancelDraft();
            return handled();
        }
        return notHandled();
    }

    public override onDeactivate(): EventHandlerResult {
        // Reached on a real tool switch (or on mouse-leave while idle —
        // isActionInProgress prevents leave-deactivation mid-draft). Either
        // way, discard any draft so no half-placed annotation lingers.
        this.cancelDraft();
        return handled();
    }

    public override dispose(): void {
        this.cancelDraft();
    }

    /** Discard any in-progress placement, for the UI's Clear all. */
    public cancelDraft(): void {
        if (this._view) {
            this._view = null;
            this._pane = null;
            this._press = null;
            this.discardDraft();
        }
    }

    /**
     * Drops the draft and warns that the image in the pane changed, so the
     * vanished draft does not read as a lost click.
     */
    protected cancelDraftForChangedImage(): void {
        this.cancelDraft();
        enqueueSnackbar(paneImageChangedMessage(), { variant: 'warning' });
    }

    /** Release the view binding once a placement has been committed. */
    protected finishPlacement(): void {
        this._view = null;
        this._pane = null;
        this._press = null;
    }

    /**
     * Whether a left press-drag-release places the next point, on top of
     * click-to-place. Off by default: a tool opts in when dragging is a
     * natural way to express what it places and nothing else in the tool
     * claims a left drag.
     */
    protected placesPointOnDragRelease(): boolean {
        return false;
    }

    /**
     * Why the tool refuses to start a placement on `view`, which shows
     * `pane`, or null to accept. A refused press is consumed and the reason
     * shown as a warning, so it neither pans the tile nor passes without a
     * word. Accepts every view by default.
     */
    protected placementRefusal(
        _view: PlaneView,
        _pane: ShownPaneImage
    ): string | null {
        return null;
    }

    /**
     * Whether `view` still shows what it showed at the first press: the same
     * image through the same registration. When it does not, the draft is
     * dropped with a warning, as its points and its record would be keyed to
     * an image the pane no longer shows where it did.
     */
    private paneStillShown(view: PlaneView): boolean {
        if (this._pane && samePaneImage(this._pane, paneImageOf(view))) {
            return true;
        }
        this.cancelDraftForChangedImage();
        return false;
    }

    private placeIfPaneStillShown(view: PlaneView, pointMm: PointMm): void {
        if (this.paneStillShown(view)) {
            this.placePoint(view, pointMm);
        }
    }

    /** The clicked window position as a patient-frame mm point. */
    private patientPointAt(
        view: PlaneView,
        coordinates: ReadonlyVec2
    ): PointMm {
        return toPointMm(view.clientToPatientCoordinates(coordinates));
    }

    /**
     * Whether a press is on the point last placed. Compared in mm at the
     * view's current zoom, so a zoom or pan in between does not lose the
     * point.
     */
    private isOnLastPlacedPoint(view: PlaneView, pointMm: PointMm): boolean {
        if (!this.placesPointOnDragRelease()) {
            return false;
        }
        const lastPlaced = this.lastPlacedPoint();
        return (
            lastPlaced !== null &&
            euclideanDistanceMm(lastPlaced, pointMm) <
                DRAG_PLACEMENT_THRESHOLD_PX * view.resolution[0]
        );
    }

    /**
     * First click. Seed the subclass's points and emit the draft. `plane`
     * and `pointMm` are in the pane's frame; `pane` is what the view showed
     * at this press, to map the draft and the record into the image's own
     * frame.
     *
     * @returns false to refuse the placement (the tool stays idle).
     */
    protected abstract startPlacement(
        view: PlaneView,
        plane: AnnotationPlane,
        pointMm: PointMm,
        pane: ShownPaneImage
    ): boolean;

    /** A later click in the bound view: extend the draft, or commit it. */
    protected abstract placePoint(view: PlaneView, pointMm: PointMm): void;

    /** The cursor moved in the bound view while drafting. */
    protected abstract moveCursor(view: PlaneView, pointMm: PointMm): void;

    /** Right-click while drafting: step back, or `cancelDraft()`. */
    protected abstract undoPoint(): void;

    /** The draft's most recent point, or null when there is no draft. */
    protected abstract lastPlacedPoint(): PointMm | null;

    /** Drop the subclass's draft state and emit a null draft. */
    protected abstract discardDraft(): void;
}
